import posthog from "posthog-js";

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const REDACTED_EMAIL = "[redacted_email]";
const NOISY_EXCEPTION_SNIPPETS = [
  "resizeobserver loop",
  "non-error promise rejection",
  "aborterror",
  "failed to fetch",
  "invalid or unexpected token",
  "unexpected eof",
  "event captured as exception",
];

type BeforeSendEvent = {
  event?: string;
  properties?: Record<string, unknown>;
};

function sanitizeUnknown<T>(value: T, depth = 0): T {
  if (typeof value === "string") {
    return value.replace(EMAIL_PATTERN, REDACTED_EMAIL) as T;
  }

  if (depth >= 3 || value == null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeUnknown(item, depth + 1)) as T;
  }

  if (typeof value === "object") {
    const sanitizedEntries = Object.entries(value).map(([key, nestedValue]) => [
      key,
      sanitizeUnknown(nestedValue, depth + 1),
    ]);

    return Object.fromEntries(sanitizedEntries) as T;
  }

  return value;
}

function getExceptionMessageFromEvent(event: BeforeSendEvent): string {
  if (!event.properties) {
    return "";
  }

  const messages: string[] = [];
  const directMessage = event.properties.$exception_message;

  if (typeof directMessage === "string") {
    messages.push(directMessage);
  }

  const genericMessage = event.properties.message;

  if (typeof genericMessage === "string") {
    messages.push(genericMessage);
  }

  const exceptionList = event.properties.$exception_list;

  if (Array.isArray(exceptionList)) {
    for (const item of exceptionList) {
      if (
        item &&
        typeof item === "object" &&
        "value" in item &&
        typeof item.value === "string"
      ) {
        messages.push(item.value);
      }
    }
  }

  return messages.join(" ").toLowerCase();
}

function shouldDropNoisyException(event: BeforeSendEvent): boolean {
  if (event.event !== "$exception") {
    return false;
  }

  const exceptionMessage = getExceptionMessageFromEvent(event);

  if (!exceptionMessage) {
    return false;
  }

  return NOISY_EXCEPTION_SNIPPETS.some((snippet) =>
    exceptionMessage.includes(snippet)
  );
}

let initialized = false;

const CONSENT_STORAGE_KEY = "hipelife.analyticsConsent";

function getStoredConsent(): "accepted" | "declined" | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return value === "accepted" || value === "declined" ? value : null;
}

export function isPostHogInitialized(): boolean {
  return initialized;
}

/**
 * Initialize PostHog after React hydration. Must not run from
 * instrumentation-client.ts — that injects <script> tags into <body> before
 * hydration and causes mismatches with SSR markup (e.g. JSON-LD).
 */
export function initPostHog(): void {
  if (initialized || !posthogKey) {
    return;
  }

  initialized = true;

  posthog.init(posthogKey, {
    api_host: "/ingest",
    ui_host: "https://eu.posthog.com",
    defaults: "2025-05-24",
    opt_out_capturing_by_default: true,
    capture_exceptions: true,
    before_send: (event) => {
      if (!event) return null;
      if (shouldDropNoisyException(event)) {
        return null;
      }
      if (event.properties) {
        event.properties = sanitizeUnknown(
          event.properties
        ) as typeof event.properties;
      }
      return event;
    },
    debug: process.env.NODE_ENV === "development",
  });

  if (getStoredConsent() === "accepted") {
    posthog.opt_in_capturing();
  }
}
